import { Fragment, useEffect, useRef, useState } from 'react';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { CustomMark } from './CustomMark';
import { FixedRatesEditor } from './FixedRatesEditor';
import { FixedRtpValue } from './FixedRtpValue';
import { LevelRtpCell } from './LevelRtpCell';
import { PackBucketsEditor } from './PackBucketsEditor';
import { PercentInput } from './PercentInput';
import {
  avgCardValue,
  buybackEffective,
  canEditBuckets,
  canSetFixedRates,
  canSetLevelRtp,
  canSetRtp,
  fixedRate,
  formatMoney,
  hasFixedRates,
  isCustomPack,
  MAX_LOOT_RTP,
  priceLabel,
  pricingPatchForRtp,
  restockingHint,
  RTP_LEVELS,
  rtpEffective,
  samePercent,
  type BucketsAccess,
  type FixedRatesAccess,
  type FixedRatesResult,
  type PricingPatch,
} from './pricing';

type Props = {
  configs: PoolOfferConfig[];
  /** Values most packs share; a pack that differs is marked "custom". */
  defaultRtp: number | null;
  defaultBuyback: number | null;
  /** Resolves to false when the value was refused, so the input shows the saved one again. */
  onSave: (poolOfferId: number, patch: PricingPatch) => Promise<boolean | void>;
  /** The operator's /me flags; fixed rates are offered only with allow_fixed_rates. */
  operator: FixedRatesAccess | null;
  onSaveFixedRates: (poolOfferId: number, patch: PricingPatch) => Promise<FixedRatesResult>;
  /** The /me flag for the bucket editor, allow_pack_buckets. */
  bucketsAccess: BucketsAccess | null;
  onBucketsError: (message: string) => void;
  /** Ticked packs — the defaults block then applies to these only. */
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
};

const STATUS_LABELS: Record<string, string> = {
  enabled: 'Live',
  staged: 'Not published',
  restocking: 'Restocking',
};

const th = 'px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[var(--co-muted)]';
const td = 'px-3 py-2 align-middle';

function SelectAll({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // indeterminate has no HTML attribute; it is a DOM property only.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label="Select all packs"
      className="h-4 w-4 cursor-pointer accent-[var(--co-brand)]"
    />
  );
}

export function PricingTable({
  configs,
  defaultRtp,
  defaultBuyback,
  onSave,
  operator,
  onSaveFixedRates,
  bucketsAccess,
  onBucketsError,
  selectedIds,
  onSelectionChange,
}: Props) {
  // A block opened once stays mounted while folded (false), so the figures typed in it survive.
  const [fixedRatesOpen, setFixedRatesOpen] = useState<Record<number, boolean>>({});
  const [bucketsOpen, setBucketsOpen] = useState<Record<number, boolean>>({});
  const selected = new Set(selectedIds);
  const allSelected = configs.length > 0 && configs.every((c) => selected.has(c.pool_offer_id));

  function toggle(id: number) {
    onSelectionChange(
      selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id],
    );
  }

  function toggleFixedRates(id: number) {
    setFixedRatesOpen((open) => ({ ...open, [id]: !open[id] }));
  }

  function toggleBuckets(id: number) {
    setBucketsOpen((open) => ({ ...open, [id]: !open[id] }));
  }

  const headers = [
    <th key="select" className={`${th} w-10`}>
      <SelectAll
        checked={allSelected}
        indeterminate={selectedIds.length > 0 && !allSelected}
        onChange={() => onSelectionChange(allSelected ? [] : configs.map((c) => c.pool_offer_id))}
      />
    </th>,
    <th key="pack" className={th}>
      Pack
    </th>,
    <th key="price" className={`${th} text-right`}>
      Price
    </th>,
    <th key="rtp_normal" className={th} title="RTP of the Normal level, the pack's own RTP">
      RTP Normal
    </th>,
    ...RTP_LEVELS.map((level) => (
      <th key={level.field} className={th} title={`RTP of the ${level.label} level. Muted = same as Normal.`}>
        RTP {level.label}
      </th>
    )),
    <th
      key="avg_card_value"
      className={`${th} text-right`}
      title="Average value of cards in one pack at the Normal RTP"
    >
      Avg card value
    </th>,
    <th key="buyback" className={th}>
      Buyback
    </th>,
    <th key="status" className={th}>
      Status
    </th>,
  ];

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <table className="w-full min-w-[960px] border-collapse text-[13px] text-[var(--co-text)]">
        <thead className="border-b border-[var(--co-border)]">
          <tr>{headers}</tr>
        </thead>
        <tbody>
          {configs.map((config) => {
            const name = config.title ?? config.pool_offer_title;
            const rtp = rtpEffective(config);
            const buyback = buybackEffective(config);
            const avgValue = avgCardValue(config, rtp);
            const fixed = hasFixedRates(config);
            const rtpCustom = defaultRtp != null && rtp != null && !samePercent(rtp, defaultRtp);
            const buybackCustom = defaultBuyback != null && !samePercent(buyback, defaultBuyback);
            const fixedRatesEditable = canSetFixedRates(operator, config);
            const fixedRatesMounted = fixedRatesEditable && fixedRatesOpen[config.pool_offer_id] != null;
            const fixedRatesShown = fixedRatesEditable && fixedRatesOpen[config.pool_offer_id] === true;
            const fixedRatesId = `fixed-rates-${config.pool_offer_id}`;
            const bucketsEditable = canEditBuckets(bucketsAccess, config);
            const bucketsMounted = bucketsEditable && bucketsOpen[config.pool_offer_id] != null;
            const bucketsShown = bucketsEditable && bucketsOpen[config.pool_offer_id] === true;
            const bucketsId = `pack-buckets-${config.pool_offer_id}`;

            return (
              <Fragment key={config.pool_offer_id}>
                <tr
                  className={`border-t border-[var(--co-border)] first:border-0 ${
                    selected.has(config.pool_offer_id) ? 'bg-[var(--co-bg)]' : ''
                  }`}
                >
                  <td className={td}>
                    <input
                      type="checkbox"
                      checked={selected.has(config.pool_offer_id)}
                      onChange={() => toggle(config.pool_offer_id)}
                      aria-label={`Select ${name}`}
                      className="h-4 w-4 cursor-pointer accent-[var(--co-brand)]"
                    />
                  </td>
                  <td className={td}>
                    <div className="font-medium">{name}</div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-[var(--co-muted)]">#{config.pool_offer_id}</span>
                      {fixedRatesEditable && (
                        <button
                          type="button"
                          aria-label={`Fixed rates for ${name}${fixed ? ' (on)' : ''}`}
                          aria-expanded={fixedRatesShown}
                          aria-controls={fixedRatesId}
                          onClick={() => toggleFixedRates(config.pool_offer_id)}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1 text-[11px] font-medium text-[var(--co-brand)] hover:bg-[var(--co-bg)]"
                        >
                          Fixed rates
                          {fixed && (
                            <span className="rounded-full bg-sky-100 px-1.5 text-[10px] font-semibold text-sky-800">
                              on
                            </span>
                          )}
                          <svg
                            viewBox="0 0 16 16"
                            className={`h-3 w-3 transition-transform ${fixedRatesShown ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                      {bucketsEditable && (
                        <button
                          type="button"
                          aria-label={`Buckets for ${name}`}
                          aria-expanded={bucketsShown}
                          aria-controls={bucketsId}
                          onClick={() => toggleBuckets(config.pool_offer_id)}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1 text-[11px] font-medium text-[var(--co-brand)] hover:bg-[var(--co-bg)]"
                        >
                          Buckets
                          <svg
                            viewBox="0 0 16 16"
                            className={`h-3 w-3 transition-transform ${bucketsShown ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                  {/* A pack with no price to measure RTP against (a $0 one too) shows a dash, as it always has. */}
                  <td className={`${td} text-right font-mono`}>{canSetRtp(config) ? priceLabel(config) : '—'}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    {fixed ? (
                      <FixedRtpValue rtp={fixedRate(config, 'rtp_normal')} />
                    ) : canSetRtp(config) ? (
                      <>
                        <PercentInput
                          label={`RTP for ${name}`}
                          value={rtp}
                          {...(isCustomPack(config) ? {} : { max: MAX_LOOT_RTP })}
                          onCommit={(v) => onSave(config.pool_offer_id, pricingPatchForRtp(config, v))}
                        />
                        {rtpCustom && (
                          <CustomMark
                            label={`Reset RTP for ${name} to ${defaultRtp}%`}
                            onReset={() =>
                              void onSave(config.pool_offer_id, pricingPatchForRtp(config, defaultRtp!))
                            }
                          />
                        )}
                      </>
                    ) : (
                      <span className="text-[12px] text-[var(--co-muted)]">No price</span>
                    )}
                  </td>
                  {RTP_LEVELS.map((level) => (
                    <td key={level.field} className={`${td} whitespace-nowrap`}>
                      {fixed ? (
                        <FixedRtpValue rtp={fixedRate(config, level.fixedRtp)} />
                      ) : canSetLevelRtp(config) ? (
                        <LevelRtpCell config={config} level={level} name={name} onSave={onSave} />
                      ) : (
                        <span className="text-[12px] text-[var(--co-muted)]">—</span>
                      )}
                    </td>
                  ))}
                  <td className={`${td} text-right font-mono`}>
                    {avgValue != null ? formatMoney(avgValue) : '—'}
                  </td>
                  <td className={`${td} whitespace-nowrap`}>
                    <PercentInput
                      label={`Buyback for ${name}`}
                      value={buyback}
                      max={100}
                      onCommit={(v) => onSave(config.pool_offer_id, { buyback_percent: v / 100 })}
                    />
                    {buybackCustom && (
                      <CustomMark
                        label={`Reset buyback for ${name} to ${defaultBuyback}%`}
                        onReset={() =>
                          void onSave(config.pool_offer_id, { buyback_percent: defaultBuyback! / 100 })
                        }
                      />
                    )}
                  </td>
                  <td
                    className={`${td} text-[12px] text-[var(--co-muted)]`}
                    title={config.availability === 'restocking' ? restockingHint(config) : undefined}
                  >
                    {STATUS_LABELS[config.availability] ?? config.availability}
                  </td>
                </tr>
                {fixedRatesEditable && (
                  <tr
                    id={fixedRatesId}
                    hidden={!fixedRatesShown}
                    className="border-t border-[var(--co-border)] bg-[var(--co-bg)]"
                  >
                    <td colSpan={headers.length} className="px-3 py-3">
                      {fixedRatesMounted && (
                        <FixedRatesEditor config={config} name={name} onSave={onSaveFixedRates} />
                      )}
                    </td>
                  </tr>
                )}
                {bucketsEditable && (
                  <tr
                    id={bucketsId}
                    hidden={!bucketsShown}
                    className="border-t border-[var(--co-border)] bg-[var(--co-bg)]"
                  >
                    <td colSpan={headers.length} className="px-3 py-3">
                      {bucketsMounted && (
                        <PackBucketsEditor
                          poolOfferId={config.pool_offer_id}
                          name={name}
                          price={config.pool_offer_price!}
                          fixedRates={fixed}
                          onError={onBucketsError}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
